import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import Pane from './pane';
import Sash from './sash';
import SashContent from './SashContent';
import {
    classNames,
    bodyDisableUserSelect,
    paneClassName,
    splitClassName,
    splitDragClassName,
    splitVerticalClassName,
    splitHorizontalClassName,
    sashDisabledClassName,
    sashHorizontalClassName,
    sashVerticalClassName,
    assertsSize
} from './base';
import { IAxis, ISplitProps, IPaneConfigs, ICacheSizes } from './types';

const Split = ({
    children,
    sizes: propSizes,
    allowResize = true,
    split = 'vertical',
    className: wrapClassName,
    resizerSize = 4,
    performanceMode = false,
    onChange = () => null,
    onDragStart = () => null,
    onDragEnd = () => null,
    ...others
}: ISplitProps) => {
    let sashRender = ((_, active) => <SashContent active={active} type='vscode' />);
    const axis = useRef<IAxis>({ x: 0, y: 0 });
    const wrapper = useRef<HTMLDivElement>(null);
    const cacheSizes = useRef<ICacheSizes>({ sizes: [], sashPosSizes: [] });
    const prevWrapSize = useRef<number>(0);
    const [wrapperRect, setWrapperRect] = useState({});
    const [isDragging, setDragging] = useState<boolean>(false);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            setWrapperRect(wrapper?.current?.getBoundingClientRect() ?? {});
        });
        resizeObserver.observe(wrapper.current!);
        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const {
        sizeName,
        splitPos,
        splitAxis
    } = useMemo(() => ({
        sizeName: split === 'vertical' ? 'width' : 'height',
        splitPos: split === 'vertical' ? 'left' : 'top',
        splitAxis: split === 'vertical' ? 'x' : 'y'
    }), [split]);

    const wrapSize: number = wrapperRect[sizeName] ?? 0;

    // Get limit sizes via children
    const paneLimitSizes = useMemo(() => children.map(childNode => {
        const limits = [0, Infinity];
        if (childNode.type === Pane) {
            const { minSize, maxSize } = childNode.props as IPaneConfigs;
            limits[0] = assertsSize(minSize, wrapSize, 0);
            limits[1] = assertsSize(maxSize, wrapSize);
        }
        return limits;
    }), [children, wrapSize]);

    const sizes = useMemo(function () {
        let count = 0;
        let curSum = 0;
        const res = children.map((child, index) => {
            let size = assertsSize(propSizes[index], wrapSize);
            // Clamp against minSize: if the resolved size is smaller than the declared
            // minimum, use the minimum (in absolute pixels) instead of the default.
            // Skip when wrapSize is 0 (container not yet measured) to avoid bogus overflow.
            if (size !== Infinity && wrapSize > 0 && child.type === Pane) {
                const { minSize } = child.props as IPaneConfigs;
                if (minSize !== undefined) {
                    const minPx = assertsSize(minSize, wrapSize, 0);
                    if (size < minPx) size = minPx;
                }
            }
            size === Infinity ? count++ : curSum += size;
            return size;
        });

        const hasPinned = children.some(child => child.type === Pane && !!(child.props as IPaneConfigs).pinned);
        const prevSize = prevWrapSize.current;
        const isContainerResize = prevSize > 0 && prevSize !== wrapSize;

        // On the first render with a known container size, convert any string (percentage) sizes to
        // absolute pixels so that pinned protection works in fixed values from the start.
        const hasStringSizes = propSizes.some(s => typeof s === 'string');
        if (prevSize === 0 && wrapSize > 0 && hasStringSizes && !isDragging) {
            setTimeout(() => onChange(res.map(s => s === Infinity ? 0 : s)), 0);
        }

        // resize or illegal size input, recalculate pane sizes
        // also trigger when container resized with pinned panes and percentage-based sizes (curSum === wrapSize)
        // Skip entirely when wrapSize is 0 (container not yet measured) to avoid emitting spurious onChange([0,0])
        // which would corrupt saved absolute-pixel sizes before the real container width is known.
        if (wrapSize > 0 && (curSum > wrapSize || (!count && curSum < wrapSize) || (hasPinned && isContainerResize))) {
            if (hasPinned) {
                // Convert propSizes at the previous container size to get the true pixel value of pinned panes
                const baseRes = children.map((_, index) =>
                    assertsSize(propSizes[index], prevSize || wrapSize)
                );
                const pinnedMask = children.map(child =>
                    child.type === Pane && !!(child.props as IPaneConfigs).pinned
                );
                const pinnedSum = baseRes.reduce((acc, size, i) =>
                    pinnedMask[i] && size !== Infinity ? acc + size : acc, 0
                );
                const nonPinnedCurSum = baseRes.reduce((acc, size, i) =>
                    !pinnedMask[i] && size !== Infinity ? acc + size : acc, 0
                );
                const nonPinnedAvail = wrapSize - pinnedSum;
                const result = baseRes.map((size, i) => {
                    if (pinnedMask[i]) return size;
                    if (size === Infinity) return 0;
                    return nonPinnedCurSum > 0 ? size * nonPinnedAvail / nonPinnedCurSum : nonPinnedAvail;
                });
                if (!isDragging) {
                    setTimeout(() => onChange(result), 0);
                }
                return result;
            }

            const cacheNum = (curSum - wrapSize) / curSum;
            const result = res.map(size => {
                return size === Infinity ? 0 : size - size * cacheNum;
            });
            if (!isDragging) {
                setTimeout(() => onChange(result), 0);
            }
            return result;
        }

        if (count > 0) {
            const average = (wrapSize - curSum) / count;
            return res.map(size => {
                return size === Infinity ? average : size;
            });
        }

        return res;
    }, [...propSizes, children.length, wrapSize]);

    useEffect(() => {
        prevWrapSize.current = wrapSize;
    }, [wrapSize]);

    const sashPosSizes = useMemo(() => (
        sizes.reduce((a, b) => [...a, a[a.length - 1] + b], [0])
    ), [sizes]);

    const dragStart = useCallback(function (e) {
        document?.body?.classList?.add(bodyDisableUserSelect);
        axis.current = { x: e.pageX, y: e.pageY };
        cacheSizes.current = { sizes, sashPosSizes };
        setDragging(true);
        onDragStart(e);
    }, [onDragStart, sizes, sashPosSizes]);

    const dragEnd = useCallback(function (e) {
        document?.body?.classList?.remove(bodyDisableUserSelect);
        axis.current = { x: e.pageX, y: e.pageY };
        cacheSizes.current = { sizes, sashPosSizes };
        setDragging(false);
        onDragEnd(e);
    }, [onDragEnd, sizes, sashPosSizes]);

    const onDragging = useCallback(function (e, i) {
        const curAxis = { x: e.pageX, y: e.pageY };
        let distanceX = curAxis[splitAxis] - axis.current[splitAxis];

        const leftBorder = -Math.min(
            sizes[i] - paneLimitSizes[i][0],
            paneLimitSizes[i + 1][1] - sizes[i + 1]
        );
        const rightBorder = Math.min(
            sizes[i + 1] - paneLimitSizes[i + 1][0],
            paneLimitSizes[i][1] - sizes[i]
        );

        if (distanceX < leftBorder) {
            distanceX = leftBorder;
        }
        if (distanceX > rightBorder) {
            distanceX = rightBorder;
        }

        const nextSizes = [...sizes];
        nextSizes[i] += distanceX;
        nextSizes[i + 1] -= distanceX;

        onChange(nextSizes);
    }, [paneLimitSizes, onChange]);

    const paneFollow = !(performanceMode && isDragging);
    const paneSizes = paneFollow ? sizes : cacheSizes.current.sizes;
    const panePoses = paneFollow ? sashPosSizes:  cacheSizes.current.sashPosSizes;

    return (
        <div
            className={classNames(
                splitClassName,
                split === 'vertical' && splitVerticalClassName,
                split === 'horizontal' && splitHorizontalClassName,
                isDragging && splitDragClassName,
                wrapClassName
            )}
            ref={wrapper}
            {...others}
        >
            {children.map((childNode, childIndex) => {
                const isPane = childNode.type === Pane;
                const paneProps = isPane ? childNode.props : {};

                return (
                    <Pane
                        key={childIndex}
                        className={classNames(paneClassName, paneProps.className)}
                        style={{
                            ...paneProps.style,
                            [sizeName]: paneSizes[childIndex],
                            [splitPos]: panePoses[childIndex]
                        }}
                    >
                        {isPane ? paneProps.children : childNode}
                    </Pane>
                );
            })}
            {sashPosSizes.slice(1, -1).map((posSize, index) => (
                <Sash
                    key={index}
                    className={classNames(
                        !allowResize && sashDisabledClassName,
                        split === 'vertical'
                            ? sashVerticalClassName
                            : sashHorizontalClassName
                    )}
                    style={{
                        [sizeName]: resizerSize,
                        [splitPos]: posSize- resizerSize / 2 - 6
                    }}
                    render={sashRender.bind(null, index)}
                    onDragStart={dragStart}
                    onDragging={e => onDragging(e, index)}
                    onDragEnd={dragEnd}
                />
            ))}
        </div>
    );
};

export default Split;
